import React, { useMemo, useRef, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
    FORMAT_TEXT_COMMAND,
    UNDO_COMMAND,
    REDO_COMMAND,
    $getSelection,
    $isRangeSelection,
    $createParagraphNode,
    $getRoot,
} from "lexical";
import { $setBlocksType } from "@lexical/selection";
import { $createHeadingNode } from "@lexical/rich-text";
import {
    INSERT_UNORDERED_LIST_COMMAND,
    INSERT_ORDERED_LIST_COMMAND,
    REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { INSERT_TABLE_COMMAND } from "@lexical/table";
import { INSERT_HORIZONTAL_RULE_COMMAND } from "@lexical/react/LexicalHorizontalRuleNode";
import { $generateNodesFromDOM } from "@lexical/html";
import { $createImageNode } from "./nodes/ImageNode";

/** Small table grid picker */
function TableGridPicker({ open, onClose, onPick, maxRows = 8, maxCols = 8 }) {
    const [hoverRow, setHoverRow] = useState(0);
    const [hoverCol, setHoverCol] = useState(0);

    // Hooks must be called before conditional returns
    const rows = useMemo(() => Array.from({ length: maxRows }), [maxRows]);
    const cols = useMemo(() => Array.from({ length: maxCols }), [maxCols]);

    if (!open) return null;

    return (
        <div
            className="position-absolute border rounded p-2 shadow-sm rte-table-picker"
            style={{ zIndex: 1000, top: "110%", left: 0, width: "auto" }}
            onMouseLeave={() => { setHoverRow(0); setHoverCol(0); }}
        >
            <div className="mb-1 small text-muted">
                {Math.max(1, hoverRow)} × {Math.max(1, hoverCol)}
            </div>
            <div role="grid" aria-label="Insert table size" className="d-inline-block">
                {rows.map((_, r) => (
                    <div key={r} className="d-flex">
                        {cols.map((__, c) => {
                            const active = r <= Math.max(0, hoverRow - 1) && c <= Math.max(0, hoverCol - 1);
                            return (
                                <button
                                    key={c}
                                    type="button"
                                    aria-label={`${r + 1} by ${c + 1}`}
                                    className={`p-0 border rte-table-cell-pick ${active ? "active" : ""}`}
                                    style={{ width: 18, height: 18, margin: 2, borderRadius: 2 }}
                                    onMouseEnter={() => { setHoverRow(r + 1); setHoverCol(c + 1); }}
                                    onClick={() => { onPick(r + 1, c + 1); onClose?.(); }}
                                />
                            );
                        })}
                    </div>
                ))}
            </div>
            <div className="text-end mt-2">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
                    Close
                </button>
            </div>
        </div>
    );
}

export default function ToolbarPlugin() {
    const [editor] = useLexicalComposerContext();
    const [block, setBlock] = useState("paragraph");

    // image file input
    const fileInputRef = useRef(null);

    // table picker
    const [tableOpen, setTableOpen] = useState(false);
    const tableBtnWrapRef = useRef(null);

    const exec = (command, payload) => editor.dispatchCommand(command, payload);

    const setParagraph = () => {
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                $setBlocksType(selection, () => $createParagraphNode());
            }
        });
        setBlock("paragraph");
    };

    const setHeading = (tag) => {
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                $setBlocksType(selection, () => $createHeadingNode(tag));
            }
        });
        setBlock(tag);
    };

    const insertHR = () => exec(INSERT_HORIZONTAL_RULE_COMMAND);

    /** Insert table with chosen rows/cols */
    const pickTable = (rows, cols) => {
        exec(INSERT_TABLE_COMMAND, { rows, columns: cols });
    };

    /** Open file picker for images */
    const openFilePicker = () => {
        fileInputRef.current?.click();
    };

    /** Handle chosen image file(s) — converts to base64 and embeds */
    const onFilesPicked = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Basic guardrails
        const isImage = file.type.startsWith("image/");
        const maxSizeMB = 10;
        if (!isImage) {
            alert("Please choose an image file.");
            e.target.value = "";
            return;
        }
        if (file.size > maxSizeMB * 1024 * 1024) {
            alert(`Image is larger than ${maxSizeMB} MB.`);
            e.target.value = "";
            return;
        }

        // Read file -> data URL (contains base64)
        const toDataUrl = (f) =>
            new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result); // "data:image/png;base64,...."
                reader.onerror = reject;
                reader.readAsDataURL(f);
            });

        try {
            const dataUrl = await toDataUrl(file);

            // If you ever need the raw base64 string separately:
            // const base64 = String(dataUrl).split(",")[1]; // everything after the first comma

            // Insert <img src="data:image/...;base64,..." />
            editor.update(() => {
                const node = $createImageNode(String(dataUrl), file.name || "");
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                    selection.insertNodes([node]);
                } else {
                    $getRoot().append(node);
                }
            });
        } catch (err) {
            console.error("Failed to read image file", err);
        } finally {
            // reset file input so the same file can be picked again
            e.target.value = "";
        }
    };

    return (
        <div className="rte-toolbar d-flex gap-2 align-items-center border-bottom px-2 py-1 flex-wrap position-relative">
            {/* hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/bmp,image/tiff,image/x-icon,image/vnd.microsoft.icon"
                onChange={onFilesPicked}
                hidden
            />

            {/* Undo / Redo */}
            <button
                type="button"
                className="btn btn-sm btn-rte"
                onClick={() => exec(UNDO_COMMAND)}
                title="Undo"
            >
                <i className="bi bi-arrow-counterclockwise" />
            </button>
            <button
                type="button"
                className="btn btn-sm btn-rte"
                onClick={() => exec(REDO_COMMAND)}
                title="Redo"
            >
                <i className="bi bi-arrow-clockwise" />
            </button>

            <div className="vr mx-1" />

            {/* Paragraph / Headings */}
            <div className="btn-group">
                <button
                    type="button"
                    className={`btn btn-sm btn-rte ${block === "paragraph" ? "active" : ""}`}
                    onClick={setParagraph}
                    title="Paragraph"
                >
                    <i className="bi bi-paragraph" />
                </button>
                <button
                    type="button"
                    className={`btn btn-sm btn-rte ${block === "h1" ? "active" : ""}`}
                    onClick={() => setHeading("h1")}
                    title="Heading 1"
                >
                    H1
                </button>
                <button
                    type="button"
                    className={`btn btn-sm btn-rte ${block === "h2" ? "active" : ""}`}
                    onClick={() => setHeading("h2")}
                    title="Heading 2"
                >
                    H2
                </button>
                <button
                    type="button"
                    className={`btn btn-sm btn-rte ${block === "h3" ? "active" : ""}`}
                    onClick={() => setHeading("h3")}
                    title="Heading 3"
                >
                    H3
                </button>
            </div>

            <div className="vr mx-1" />

            {/* Inline styles */}
            <button type="button" className="btn btn-sm btn-rte" onClick={() => exec(FORMAT_TEXT_COMMAND, "bold")} title="Bold">
                <i className="bi bi-type-bold" />
            </button>
            <button type="button" className="btn btn-sm btn-rte" onClick={() => exec(FORMAT_TEXT_COMMAND, "italic")} title="Italic">
                <i className="bi bi-type-italic" />
            </button>
            <button type="button" className="btn btn-sm btn-rte" onClick={() => exec(FORMAT_TEXT_COMMAND, "underline")} title="Underline">
                <i className="bi bi-type-underline" />
            </button>
            <button type="button" className="btn btn-sm btn-rte" onClick={() => exec(FORMAT_TEXT_COMMAND, "strikethrough")} title="Strikethrough">
                <i className="bi bi-type-strikethrough" />
            </button>

            <div className="vr mx-1" />

            {/* Lists */}
            <button
                type="button"
                className="btn btn-sm btn-rte"
                onClick={() => exec(INSERT_UNORDERED_LIST_COMMAND)}
                title="Bulleted list"
            >
                <i className="bi bi-list-ul" />
            </button>
            <button
                type="button"
                className="btn btn-sm btn-rte"
                onClick={() => exec(INSERT_ORDERED_LIST_COMMAND)}
                title="Numbered list"
            >
                <i className="bi bi-list-ol" />
            </button>
            <button
                type="button"
                className="btn btn-sm btn-rte"
                onClick={() => exec(REMOVE_LIST_COMMAND)}
                title="Remove list"
            >
                <i className="bi bi-x-lg" />
            </button>

            <div className="vr mx-1" />

            {/* Table picker (button + popover) */}
            <div className="position-relative" ref={tableBtnWrapRef}>
                <button
                    type="button"
                    className="btn btn-sm btn-rte"
                    onClick={() => setTableOpen((s) => !s)}
                    title="Insert table"
                >
                    <i className="bi bi-table" />
                </button>
                <TableGridPicker
                    open={tableOpen}
                    onClose={() => setTableOpen(false)}
                    onPick={(rows, cols) => pickTable(rows, cols)}
                    maxRows={8}
                    maxCols={8}
                />
            </div>

            {/* Image (file picker) */}
            <button
                type="button"
                className="btn btn-sm btn-rte"
                onClick={openFilePicker}
                title="Insert image"
            >
                <i className="bi bi-image" />
            </button>
        </div>
    );
}
